import { z } from 'zod';

export type ChartSpecType =
  | 'column'
  | 'stacked_column'
  | 'stacked_column_100'
  | 'bar'
  | 'stacked_bar'
  | 'stacked_bar_100'
  | 'line'
  | 'area'
  | 'combo'
  | 'waterfall'
  | 'football_field'
  | 'pie'
  | 'donut'
  | 'scatter';

export type NumberScale = 'none' | 'thousands' | 'millions' | 'billions';

export type NegativeStyle = 'minus' | 'parens';

export interface NumberFormat {
  prefix?: string;
  suffix?: string;
  decimals?: number;
  scale?: NumberScale;
  signed?: boolean;
  negativeStyle?: NegativeStyle;
}

export type ChartSeriesType = 'bar' | 'line' | 'area' | 'scatter';
export type ChartLineDash = 'solid' | 'dashed' | 'dotted';
export type ChartPointShape =
  | 'circle'
  | 'square'
  | 'triangle'
  | 'diamond'
  | 'none';

export interface ChartSeriesLineStyle {
  dash?: ChartLineDash;
  width?: number;
}

export interface ChartSeriesPoint {
  shape?: ChartPointShape;
  size?: number;
}

export interface ChartSeriesSpec {
  name: string;
  /** A null value renders as a gap (blank/error cell in a linked sheet), never 0. */
  values: Array<number | null>;
  /** X coordinates for scatter (paired with `values`); ignored by other types. */
  xValues?: Array<number | null>;
  type?: ChartSeriesType;
  stack?: string;
  axis?: 'left' | 'right';
  color?: string;
  lineStyle?: ChartSeriesLineStyle;
  point?: ChartSeriesPoint;
  dataLabels?: boolean;
}

export interface ChartAxisRange {
  min?: number;
  max?: number;
  log?: boolean;
  tickFormat?: NumberFormat;
  gridLines?: boolean;
  title?: string;
}

export interface ChartAxisSpec {
  xAxis?: ChartAxisRange;
  yAxisLeft?: ChartAxisRange;
  yAxisRight?: ChartAxisRange;
}

export interface ChartTextStyle {
  text?: string;
  size?: number;
  weight?: number;
  italic?: boolean;
  align?: 'left' | 'center' | 'right';
  color?: string;
}

export interface ChartTitleStyle {
  title?: ChartTextStyle;
  subtitle?: ChartTextStyle;
}

export type ChartLegendVisibility = 'auto' | 'show' | 'hide';
export type ChartLegendPosition = 'top' | 'bottom' | 'left' | 'right';

export interface ChartLegendSpec {
  show?: ChartLegendVisibility;
  position?: ChartLegendPosition;
  size?: number;
  color?: string;
}

export interface ChartStyleSpec {
  background?: string;
  border?: string;
  fontFamily?: string;
  smooth?: boolean;
  gridLines?: boolean;
}

export interface FootballFieldRow {
  label: string;
  low: number;
  high: number;
  midpoint?: number;
  color?: string;
}

export interface ChartReferenceLine {
  value: number;
  label?: string;
  color?: string;
}

export interface WaterfallColors {
  increase?: string;
  decrease?: string;
  total?: string;
}

/**
 * How a waterfall draws each category: a `step` floats from the running
 * total; a `total` is a full bar from zero that resets the running total.
 */
export type WaterfallBar = 'step' | 'total';

/**
 * Link to the backing workbook sheet that owns this chart's data. The spec's
 * `categories`/`series[].values` stay populated as a cached snapshot so slides
 * remain self-contained; sync rewrites them from the sheet and advances
 * `syncedAtSequence` to the workbook's head sequence at that moment.
 */
export interface ChartDataSource {
  workbookId: string;
  sheetId: string;
  syncedAtSequence: number;
}

export interface ChartSpec {
  type: ChartSpecType;
  title?: string;
  subtitle?: string;
  categories: string[];
  series: ChartSeriesSpec[];
  numberFormat?: NumberFormat;
  labelMode?: 'auto' | 'all' | 'none' | 'totals';
  legend?: ChartLegendSpec;
  titleStyle?: ChartTitleStyle;
  axes?: ChartAxisSpec;
  style?: ChartStyleSpec;
  /** Football-field methodology ranges (used when type === 'football_field'). */
  ranges?: FootballFieldRow[];
  referenceLine?: ChartReferenceLine;
  /** Waterfall step colors (used when type === 'waterfall'). */
  waterfallColors?: WaterfallColors;
  /** Per-category bar kind for waterfalls; absent entries are steps. */
  waterfallBars?: WaterfallBar[];
  /**
   * Pie/donut slice colors keyed by category name, so a slice keeps its color
   * when a linked sheet reorders or inserts rows.
   */
  sliceColors?: Record<string, string>;
  dataSource?: ChartDataSource;
  width?: number;
  height?: number;
}

function applyNumberScale(value: number, scale?: NumberScale): number {
  switch (scale) {
    case 'thousands':
      return value / 1e3;
    case 'millions':
      return value / 1e6;
    case 'billions':
      return value / 1e9;
    default:
      return value;
  }
}

export function formatChartValue(value: unknown, format: NumberFormat = {}) {
  const raw = Array.isArray(value) ? value[1] : value;
  if (typeof raw !== 'number') return String(raw ?? '');

  const scaled = applyNumberScale(raw, format.scale);
  const decimals = Math.max(
    0,
    format.decimals ?? (Number.isInteger(scaled) ? 0 : 1)
  );
  const absolute = Math.abs(scaled);
  const formatted = absolute.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  const prefix = format.prefix ?? '';
  const suffix = format.suffix ?? '';
  const suffixGap = suffix === 'bps' || suffix === 'pts' ? ' ' : '';
  const core = `${prefix}${formatted}${suffixGap}${suffix}`;

  // A negative value always shows its sign (or parentheses); `signed` only
  // controls whether positives get an explicit `+`.
  const negative = scaled < 0;
  if (negative && format.negativeStyle === 'parens') return `(${core})`;
  const sign = negative ? '-' : format.signed && scaled > 0 ? '+' : '';

  return `${sign}${core}`;
}

export interface ChartRenderResult {
  success: boolean;
  svg?: string;
  error?: string;
}

export const NumberScaleSchema = z.enum([
  'none',
  'thousands',
  'millions',
  'billions',
]);

export const NumberFormatSchema: z.ZodType<NumberFormat> = z.object({
  prefix: z.string().optional(),
  suffix: z.string().optional(),
  decimals: z.number().int().min(0).max(6).optional(),
  scale: NumberScaleSchema.optional(),
  signed: z.boolean().optional(),
  negativeStyle: z.enum(['minus', 'parens']).optional(),
});

export const ChartSeriesSpecSchema: z.ZodType<ChartSeriesSpec> = z.object({
  name: z.string().min(1),
  values: z.array(z.number().nullable()),
  xValues: z.array(z.number().nullable()).optional(),
  type: z.enum(['bar', 'line', 'area', 'scatter']).optional(),
  stack: z.string().optional(),
  axis: z.enum(['left', 'right']).optional(),
  color: z.string().optional(),
  lineStyle: z
    .object({
      dash: z.enum(['solid', 'dashed', 'dotted']).optional(),
      width: z.number().positive().optional(),
    })
    .optional(),
  point: z
    .object({
      shape: z
        .enum(['circle', 'square', 'triangle', 'diamond', 'none'])
        .optional(),
      size: z.number().nonnegative().optional(),
    })
    .optional(),
  dataLabels: z.boolean().optional(),
});

const ChartAxisRangeSchema: z.ZodType<ChartAxisRange> = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
  log: z.boolean().optional(),
  tickFormat: NumberFormatSchema.optional(),
  gridLines: z.boolean().optional(),
  title: z.string().optional(),
});

const ChartTextStyleSchema: z.ZodType<ChartTextStyle> = z.object({
  text: z.string().optional(),
  size: z.number().positive().optional(),
  weight: z.number().optional(),
  italic: z.boolean().optional(),
  align: z.enum(['left', 'center', 'right']).optional(),
  color: z.string().optional(),
});

export const ChartTitleStyleSchema: z.ZodType<ChartTitleStyle> = z.object({
  title: ChartTextStyleSchema.optional(),
  subtitle: ChartTextStyleSchema.optional(),
});

export const ChartAxesSchema: z.ZodType<ChartAxisSpec> = z.object({
  xAxis: ChartAxisRangeSchema.optional(),
  yAxisLeft: ChartAxisRangeSchema.optional(),
  yAxisRight: ChartAxisRangeSchema.optional(),
});

export const ChartStyleSpecSchema: z.ZodType<ChartStyleSpec> = z.object({
  background: z.string().optional(),
  border: z.string().optional(),
  fontFamily: z.string().optional(),
  smooth: z.boolean().optional(),
  gridLines: z.boolean().optional(),
});

export const ChartDataSourceSchema: z.ZodType<ChartDataSource> = z.object({
  workbookId: z.string().min(1),
  sheetId: z.string().min(1),
  syncedAtSequence: z.number().int().nonnegative(),
});

const FootballFieldRowSchema: z.ZodType<FootballFieldRow> = z.object({
  // Blank when the label cell in the chart's data tab was cleared.
  label: z.string(),
  low: z.number(),
  high: z.number(),
  midpoint: z.number().optional(),
  color: z.string().optional(),
});

const CHART_SPEC_TYPES = [
  'column',
  'stacked_column',
  'stacked_column_100',
  'bar',
  'stacked_bar',
  'stacked_bar_100',
  'line',
  'area',
  'combo',
  'waterfall',
  'football_field',
  'pie',
  'donut',
  'scatter',
] as const;

export const ChartSpecSchema: z.ZodType<ChartSpec> = z
  .object({
    type: z.enum(CHART_SPEC_TYPES),
    title: z.string().optional(),
    subtitle: z.string().optional(),
    categories: z.array(z.string()),
    series: z.array(ChartSeriesSpecSchema),
    numberFormat: NumberFormatSchema.optional(),
    labelMode: z.enum(['auto', 'all', 'none', 'totals']).optional(),
    legend: z
      .object({
        show: z.enum(['auto', 'show', 'hide']).optional(),
        position: z.enum(['top', 'bottom', 'left', 'right']).optional(),
        size: z.number().positive().optional(),
        color: z.string().optional(),
      })
      .optional(),
    titleStyle: ChartTitleStyleSchema.optional(),
    axes: ChartAxesSchema.optional(),
    style: ChartStyleSpecSchema.optional(),
    ranges: z.array(FootballFieldRowSchema).optional(),
    referenceLine: z
      .object({
        value: z.number(),
        label: z.string().optional(),
        color: z.string().optional(),
      })
      .optional(),
    waterfallColors: z
      .object({
        increase: z.string().optional(),
        decrease: z.string().optional(),
        total: z.string().optional(),
      })
      .optional(),
    waterfallBars: z.array(z.enum(['step', 'total'])).optional(),
    sliceColors: z.record(z.string(), z.string()).optional(),
    dataSource: ChartDataSourceSchema.optional(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
  })
  .superRefine((spec, ctx) => {
    if (spec.type === 'football_field') {
      if (!spec.ranges || spec.ranges.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['ranges'],
          message: 'Football-field charts require at least one range.',
        });
      }
      return;
    }
    if (spec.categories.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['categories'],
        message: 'Chart requires at least one category.',
      });
    }
    if (spec.series.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['series'],
        message: 'Chart requires at least one series.',
      });
    }
    if (
      spec.waterfallBars &&
      spec.waterfallBars.length !== spec.categories.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['waterfallBars'],
        message: 'Waterfall bars must match category count.',
      });
    }
    for (const [index, series] of spec.series.entries()) {
      if (series.values.length !== spec.categories.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['series', index, 'values'],
          message: 'Chart series values must match category count.',
        });
      }
    }
  });
